import { API_BASE_URL, getActiveOrganizationId, getActiveUserId } from '@/api/client';

/**
 * Minimal EventSource-compatible surface backed by `fetch`. Native `EventSource` can only issue
 * GET requests with no custom headers, but the assistant's SSE endpoints are POST (need a JSON
 * body for /messages) and require the `X-User-Id` header for auth (see JwtAuthenticationFilter,
 * which resolves the `at_{userId}` cookie from that header) — EventSource can't do either.
 */
export interface SSESource {
  addEventListener(type: string, listener: (event: { data: string }) => void): void;
  close(): void;
}

function parseEventBlock(block: string): { type: string; data: string } {
  let type = 'message';
  const dataLines: string[] = [];
  for (const line of block.split('\n')) {
    if (line.startsWith('event:')) {
      type = line.slice('event:'.length).trim();
    } else if (line.startsWith('data:')) {
      // Don't trim: streamed chunks legitimately start with a space (tokenizers commonly
      // attach the leading space to the next word), and the backend's SseEmitter doesn't add
      // a conventional padding space after "data:" the way some SSE servers do — trimming here
      // was eating those spaces and running words together mid-stream (e.g. "JaneTesterson").
      dataLines.push(line.slice('data:'.length));
    }
  }
  return { type, data: dataLines.join('\n') };
}

/** POSTs to `path` and exposes the response as an SSE stream, parsed incrementally as chunks arrive. */
export function postEventStream(path: string, body?: unknown): SSESource {
  const listeners = new Map<string, ((event: { data: string }) => void)[]>();
  const controller = new AbortController();
  let closed = false;

  const emit = (type: string, data: string) => {
    for (const listener of listeners.get(type) ?? []) {
      listener({ data });
    }
  };

  const headers: Record<string, string> = { Accept: 'text/event-stream' };
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  const userId = getActiveUserId();
  if (userId) headers['X-User-Id'] = userId;
  const orgId = getActiveOrganizationId();
  if (orgId) headers['X-Org-Id'] = orgId;

  (async () => {
    try {
      const response = await fetch(`${API_BASE_URL}${path}`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: body !== undefined ? JSON.stringify(body) : undefined,
        signal: controller.signal,
      });

      if (!response.ok || !response.body) {
        if (!closed) emit('error', `Request failed with status ${response.status}`);
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (!closed) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        let separatorIndex: number;
        while ((separatorIndex = buffer.indexOf('\n\n')) !== -1) {
          const rawEvent = buffer.slice(0, separatorIndex);
          buffer = buffer.slice(separatorIndex + 2);
          if (rawEvent.trim().length === 0) continue;
          const { type, data } = parseEventBlock(rawEvent);
          emit(type, data);
        }
      }
    } catch (err) {
      if (!closed) {
        emit('error', err instanceof Error ? err.message : 'Connection failed');
      }
    }
  })();

  return {
    addEventListener(type, listener) {
      const arr = listeners.get(type) ?? [];
      arr.push(listener);
      listeners.set(type, arr);
    },
    close() {
      closed = true;
      controller.abort();
    },
  };
}
